import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CardList from "../ui/CardList.jsx";
import { StatusPill } from "./queue/QueueTaskCard.jsx";
import {
  JOB_LIST_STATIONS, defaultListStartDate, jobListParams, loadCustomersByName, loadJobList, relativeDueLabel, sortJobs,
} from "../../features/jobs/jobsData";
import "../mobile.css";

// The phone Jobs list (plan Phase 1, Jobs): the desktop list's query (features/jobs/jobsData.js) as
// cards, newest job first. Search covers every job (the filters are off while searching, as on
// the desktop); Filters: customer, customer PO / SO, station, since date, shipped jobs. A card opens
// the job (/jobs/:id). The search and filters are kept for this tab, so Back from a job lands on
// the same list. Layout only.

const KEPT = "jobq.phone.jobsList";
const SHOWN = 50;

const toDateInput = (d) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` : "");
const fromDateInput = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || "");
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

const DEFAULT_FILTERS = () => ({ customerId: "", customerPo: "", customerSo: "", queueFilter: "", since: toDateInput(defaultListStartDate()), showShipped: false });

function readKept() {
  try {
    const kept = JSON.parse(sessionStorage.getItem(KEPT) || "null");
    if (kept && typeof kept === "object") return { search: kept.search || "", filters: { ...DEFAULT_FILTERS(), ...(kept.filters || {}) } };
  } catch (e) { /* none kept */ }
  return { search: "", filters: DEFAULT_FILTERS() };
}

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function JobCard({ job }) {
  const due = relativeDueLabel(job.due_date);
  const closed = ["Completed", "Shipped", "Cancelled"].includes(job.status);
  return (
    <>
      <div className="jq-qcard-top">
        <span className="jq-qcard-job">Job #{job.master_job_num || job.job_id}</span>
        <StatusPill status={job.status} />
        {job.priority_level === 1 && <span className="jq-tag jq-tag-block">P1</span>}
      </div>
      <div className="jq-qcard-title">{job.job_description}</div>
      <div className="jq-qcard-meta">
        {job.customer_name && <span>{job.customer_name}</span>}
        {due && !closed && <span style={{ color: due.color, fontWeight: 600 }}>{due.text}</span>}
        {job.sales_order_number && <span>{job.sales_order_number}</span>}
        {job.customer_po && <span>PO {job.customer_po}</span>}
        {job.quantity_ordered > 0 && <span>{job.qty_completed || 0}/{job.quantity_ordered} done</span>}
      </div>
    </>
  );
}

export default function JobsScreen() {
  const navigate = useNavigate();
  const [{ search: keptSearch, filters: keptFilters }] = useState(readKept);
  const [search, setSearch] = useState(keptSearch);
  const [filters, setFilters] = useState(keptFilters);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const debouncedSearch = useDebounced(search.trim(), 500);

  useEffect(() => {
    // The default "since" (30 days back) is not kept: tomorrow it is a day later.
    const kept = { ...filters };
    if (kept.since === DEFAULT_FILTERS().since) delete kept.since;
    try { sessionStorage.setItem(KEPT, JSON.stringify({ search, filters: kept })); } catch (e) { /* storage blocked */ }
  }, [search, filters]);

  useEffect(() => {
    loadCustomersByName().then(setCustomers).catch((err) => console.error("Error loading customers:", err));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setShowAll(false);
    const params = jobListParams({
      searchTerm: debouncedSearch,
      customerId: filters.customerId,
      customerPo: filters.customerPo.trim(),
      customerSo: filters.customerSo.trim(),
      startDate: fromDateInput(filters.since),
      queueFilter: filters.queueFilter,
      hideShipped: !filters.showShipped,
      summary: true,
    });
    loadJobList(params, { isCancelled: () => cancelled })
      .then((data) => { if (!cancelled && data !== null) setJobs(data); })
      .catch((err) => { if (!cancelled) setError(err.message || "Couldn't load the jobs."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedSearch, filters]);

  const sorted = useMemo(() => sortJobs(jobs, "job_id", "desc"), [jobs]);
  const visible = showAll ? sorted : sorted.slice(0, SHOWN);
  const defaults = DEFAULT_FILTERS();
  const activeCount = ["customerId", "customerPo", "customerSo", "queueFilter", "since", "showShipped"].filter((k) => filters[k] !== defaults[k]).length;
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const searching = Boolean(search.trim());

  return (
    <div className="jq-screen" data-testid="phone-jobs">
      {loading && jobs.length > 0 && (
        <div className="jq-muted mb-2" role="status">
          <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />Updating…
        </div>
      )}
      <CardList
        items={visible}
        getKey={(j) => j.job_id}
        renderCard={(j) => <JobCard job={j} />}
        onItemClick={(j) => navigate(`/jobs/${j.job_id}`)}
        search={{ value: search, onChange: setSearch, placeholder: "Job #, customer, PO, description" }}
        filters={{
          activeCount,
          onClear: () => setFilters(DEFAULT_FILTERS()),
          resultLabel: loading ? "Show jobs" : `Show ${sorted.length} job${sorted.length === 1 ? "" : "s"}`,
          render: () => (
            <>
              {searching && <div className="jq-note jq-note-info">These filters are off while you search.</div>}
              <div className="jq-filter-field">
                <label htmlFor="jobs-customer">Customer</label>
                <select id="jobs-customer" className="form-select" value={filters.customerId} onChange={set("customerId")}>
                  <option value="">All customers</option>
                  {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
                </select>
              </div>
              <div className="jq-filter-field">
                <label htmlFor="jobs-po">Customer PO</label>
                <input id="jobs-po" className="form-control" value={filters.customerPo} onChange={set("customerPo")} autoComplete="off" />
              </div>
              <div className="jq-filter-field">
                <label htmlFor="jobs-so">Customer SO</label>
                <input id="jobs-so" className="form-control" value={filters.customerSo} onChange={set("customerSo")} autoComplete="off" />
              </div>
              <div className="jq-filter-field">
                <label htmlFor="jobs-station">Station</label>
                <select id="jobs-station" className="form-select" value={filters.queueFilter} onChange={set("queueFilter")}>
                  <option value="">All stations</option>
                  {JOB_LIST_STATIONS.map((st) => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
              <div className="jq-filter-field">
                <label htmlFor="jobs-since">Created since</label>
                <input id="jobs-since" type="date" className="form-control" value={filters.since} onChange={set("since")} />
                <div className="jq-muted mt-1">Clear the date to see every job.</div>
              </div>
              <div className="form-check form-switch jq-filter-field">
                <input id="jobs-shipped" type="checkbox" className="form-check-input" checked={filters.showShipped} onChange={set("showShipped")} />
                <label htmlFor="jobs-shipped" className="form-check-label">Show shipped jobs</label>
              </div>
            </>
          ),
        }}
        loading={loading && jobs.length === 0}
        error={error}
        empty={searching ? `No job matches "${search.trim()}".` : "No jobs match these filters."}
        testId="phone-jobs-list"
      />
      {!showAll && sorted.length > SHOWN && (
        <button type="button" className="btn btn-outline-secondary jq-show-more" onClick={() => setShowAll(true)}>
          Show all {sorted.length}
        </button>
      )}
    </div>
  );
}
